import { hasPlanFeature } from '@/hooks/useFeatureAccess';
import { resolveBrandFromCompany } from '@/lib/brand/resolve-brand-theme';
import { connectCustomerLanguageBridge } from '@/lib/http/customer-request';
import { connectStoreBridge } from '@/lib/http/request';
import { isSignupIncomplete } from '@/lib/incomplete-signup';
import { authService } from '@/lib/services/auth.service';
import { companyService } from '@/lib/services/company.service';
import { AppShellSkeleton } from '@/pages/layout/shell/AppShellSkeleton';
import { getCurrentTenantSubdomain } from '@/utils/app-domain';
import { BOOTSTRAP_QUERY_STALE_TIME } from '@/utils/reactQueryClient';
import { updateThemeColors } from '@/utils/theme-utils';
import { withDevTools } from '@poly-state/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FC, PropsWithChildren, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { dropCompanyBootstrapQueries, prefetchCompanyBootstrapQueries } from './company-bootstrap-prefetch';
import {
	emptyWhitelabeling,
	GlobalStore,
	GlobalStoreContext,
	GlobalStoreType,
	getGlobalStoreCookie,
	globalStoreInitialState,
	resolveCurrentCompany,
	resolveLanguage,
	setGlobalStoreCookie,
	useGlobalStoreSelector,
} from './global.store';

type SelectedCompanyCookie = {
	companyId: string | null;
	token: string;
};

/** Persists the workspace resolved during hydrate, exactly as `GlobalStore.onCompanySelection` does. */
const persistSelectedCompany = ({ companyId, token }: SelectedCompanyCookie) => {
	setGlobalStoreCookie(() => ({ selectedCompanyID: companyId, token }));
};

/** The pre-session state: nothing authenticated, language from a remembered choice or the browser. */
const unauthenticatedState = (): GlobalStoreType => ({
	...globalStoreInitialState,
	language: resolveLanguage(),
});

// A refetch that finds no session keeps the brand a public seam (meeting room, custom domain) wrote
// for a guest; only a session that just ended is wiped back to the platform defaults.
const guestRefreshState = (previous: GlobalStoreType): GlobalStoreType => {
	if (previous.authenticated) return unauthenticatedState();
	return {
		...unauthenticatedState(),
		whitelabeling: previous.whitelabeling,
		resolvedBrand: previous.resolvedBrand,
		faviconOverride: previous.faviconOverride,
		publicMeetingFeatureAccess: previous.publicMeetingFeatureAccess,
	};
};

/**
 * Routes that render outside the authenticated app shell: public booking and customer pages, auth
 * screens, embeds. While hydrate is in flight they show nothing (as the app always did for them)
 * instead of an app-shell skeleton that never materializes into a shell.
 */
const SHELL_LESS_PATH_PREFIXES = [
	'/accept-invitation',
	'/book-meeting',
	'/calendar-callback',
	'/cancel-booking',
	'/customer',
	'/forgot-password',
	'/forms',
	'/itxframe',
	'/join',
	'/login',
	'/meeting-end',
	'/oauth',
	'/offer',
	'/prototype',
	'/record',
	'/recording',
	'/signup',
	'/unsubscribe-email',
] as const;

const isShellLessPath = (): boolean =>
	typeof window !== 'undefined' &&
	SHELL_LESS_PATH_PREFIXES.some((prefix) => window.location.pathname.startsWith(prefix));

/**
 * A custom (whitelabel) domain owns its resolved brand and public meeting capability through the
 * whitelabeling seam in `AppWithCustomDomain`; hydrate must not derive them from the company there,
 * or a `HYDRATE` refetch would overwrite the tenant's brand with platform-derived values.
 */
const isCustomDomainHost = (): boolean =>
	import.meta.env.VITE_APP_ENV !== 'e2e' &&
	typeof window !== 'undefined' &&
	!window.location.hostname.includes(import.meta.env.VITE_APP_DOMAIN);

let connectedStore: GlobalStore | null = null;

/**
 * The one store instance for the session, with the HTTP bridges already attached to it, so every
 * request fired from that moment on carries the store's token and language. Both connects only
 * reassign closures on a module-level object, so re-running them (StrictMode double-invoke) is
 * idempotent and costs no render pass.
 *
 * Kept for the life of the tab rather than rebuilt per hydrate run: a `HYDRATE` refetch now updates
 * the mounted tree in place, and swapping the store underneath it would resubscribe every consumer
 * and drop whatever was written straight to the store (resolved brand, meeting capability).
 */
const getConnectedStore = (): GlobalStore => {
	if (connectedStore) return connectedStore;

	const store = new GlobalStore(unauthenticatedState());

	if (import.meta.env.DEV) {
		withDevTools(store, 'GLOBAL_STORE');
	}

	connectStoreBridge(store);
	connectCustomerLanguageBridge(store);

	connectedStore = store;
	return store;
};

/**
 * Applies the active brand colour as CSS variables whenever the resolved Brand changes. Reads the
 * single `resolvedBrand` slot so the app chrome themes from the same source it renders from, and
 * clears back to the platform palette when no brand has resolved (neutral pre-resolve, AC-12).
 */
const ThemeColorApplier = () => {
	// `resolvedBrand` is null until a workspace resolves (neutral pre-resolve): paint no colour so
	// nothing branded ever swaps in after first paint. A branded workspace carries a concrete
	// themeColor; an unbranded one resolves to the platform colour, both applied verbatim.
	const brandColor = useGlobalStoreSelector((s) =>
		s.resolvedBrand ? (s.resolvedBrand.isBranded ? s.resolvedBrand.themeColor : null) : null,
	);

	useEffect(() => {
		updateThemeColors(brandColor);
	}, [brandColor]);

	return null;
};

/**
 * Keeps Sentry's user attribution in step with the session by subscribing to the store's `user`
 * slot - not to the hydrate query's `data`, which is a referentially-stable singleton and would
 * leave attribution frozen across SPA login/logout and every `HYDRATE` refetch.
 */
const SentryUserSync = () => {
	const user = useGlobalStoreSelector((s) => (s.authenticated ? s.user : null));

	useEffect(() => {
		if (user) {
			Sentry.setUser({
				id: user.id,
				email: user.email,
				ip_address: '{{auto}}',
			});
		} else {
			Sentry.setUser(null);
		}
	}, [user]);

	return null;
};

export const GlobalStoreProvider: FC<PropsWithChildren> = ({ children }) => {
	const queryClient = useQueryClient();
	const { mutateAsync: getProfile } = authService.useGetProfile();
	const { mutateAsync: getCompanies } = companyService.useGetCompanies();

	const { isLoading, data, error, refetch } = useQuery({
		queryFn: async (): Promise<GlobalStore> => {
			const cookies = getGlobalStoreCookie();
			const token = cookies?.token;

			// The store - and with it the HTTP bridges - exists before anything is requested, so the
			// session token from the cookie is on every request from t=0. That is what makes the
			// company-scoped prefetch below possible: without a connected bridge those requests would
			// go out unauthenticated. Hydrate fills the rest of the state in once it resolves.
			const store = getConnectedStore();
			store.setState((previous) => ({ ...previous, token: token ?? '' }));

			if (!cookies || !token) {
				store.setState(guestRefreshState);
				return store;
			}

			const tokenPayload = { token };

			const profilePromise = getProfile(tokenPayload).catch((e) => {
				console.log(e);
				return undefined;
			});

			const companiesPromise = getCompanies(tokenPayload).catch((e) => {
				console.log(e);
				return undefined;
			});

			// Fired in the same tick as profile/companies and never awaited: the cookie already names
			// the workspace, so the queries the app shell mounts no longer wait a full request layer
			// for profile/companies to come back before they can start.
			prefetchCompanyBootstrapQueries({ queryClient, companyId: cookies.selectedCompanyID ?? '' });

			// get more items here such as clients

			const [profile, workspaces] = await Promise.all([profilePromise, companiesPromise]);

			// An unfinished social signup holds a real session, but the server refuses every product
			// route for it with 403 `SIGNUP_INCOMPLETE` - the workspace list included, so `workspaces`
			// came back undefined. Such a User owns no workspace yet either, so an empty list is the
			// truth here; reading the refusal as a dead session instead would bounce a reload off the
			// details step and back to the address form of an account the person already has. The rule
			// itself lives in `lib/incomplete-signup.ts`, shared with the guard that does the sending.
			const companies = workspaces ?? (isSignupIncomplete(profile) ? [] : undefined);

			if (!profile || !companies) {
				// If we fail to get profile or companies, invalidate the query to prevent rehydration
				queryClient.invalidateQueries({ queryKey: ['GLOBAL_STORE_HYDRATE'] });
				store.setState(guestRefreshState);
				return store;
			}

			// Resolving the workspace here - rather than in a post-mount effect - is what lets
			// `AppLayout` render its children on the first paint after hydrate: by the time the
			// store exists it already knows which company the session is in.
			const currentCompany = resolveCurrentCompany({
				companies,
				tenantSubdomain: getCurrentTenantSubdomain(),
				selectedCompanyID: cookies.selectedCompanyID,
			});

			if (cookies.selectedCompanyID !== (currentCompany?.id ?? null)) {
				// Mirror `GlobalStore.onCompanySelection`'s cookie write so a subdomain-resolved
				// selection (and the clearing of a stale one) survives the next reload.
				persistSelectedCompany({ companyId: currentCompany?.id ?? null, token });
				dropCompanyBootstrapQueries({ queryClient, companyId: cookies.selectedCompanyID ?? '' });
			}

			// On a custom domain the whitelabeling seam (`AppWithCustomDomain`) owns the brand and the
			// public meeting capability; keep whatever it wrote so a refetch never clobbers them.
			const onCustomDomain = isCustomDomainHost();

			store.setState((previous) => ({
				...previous,
				authenticated: !!profile,
				token,
				companies,
				currentCompany,
				user: profile,
				clients: [],
				language: resolveLanguage(profile.language),
				whitelabeling: currentCompany?.whiteLabeling || emptyWhitelabeling,
				resolvedBrand: onCustomDomain ? previous.resolvedBrand : resolveBrandFromCompany(currentCompany),
				faviconOverride: onCustomDomain ? previous.faviconOverride : null,
				publicMeetingFeatureAccess: onCustomDomain
					? previous.publicMeetingFeatureAccess
					: {
							hasDrawing: hasPlanFeature(currentCompany?.companyBillingEntity?.plan, 'drawing'),
						},
				showOverly: false,
			}));

			return store;
		},
		queryKey: ['GLOBAL_STORE_HYDRATE'],
		// Profile + workspace list are not worth re-requesting on every remount of the provider;
		// the two things that genuinely change them (a `HYDRATE` event, an explicit invalidation)
		// both refetch regardless of staleness.
		staleTime: BOOTSTRAP_QUERY_STALE_TIME,
	});

	useEffect(() => {
		const listener = () => refetch();
		window.addEventListener('HYDRATE', listener);
		return () => window.removeEventListener('HYDRATE', listener);
	}, [refetch]);

	// Deliberately not gated on `isFetching`: a `HYDRATE` refetch (plan change, billing update)
	// leaves `data` in place, so the mounted tree keeps rendering and updates when the refetch
	// lands, instead of unmounting the whole app to a blank page and back.
	// Shell-less routes (public booking, auth screens, embeds) get nothing while hydrate is in
	// flight - an app-shell skeleton would be the wrong shape and never materialize for them.
	if (isLoading || !data) return isShellLessPath() ? null : <AppShellSkeleton />;

	if (error) return <p>error...</p>;

	return (
		<GlobalStoreContext.Provider value={data}>
			<ThemeColorApplier />
			<SentryUserSync />
			{children}
		</GlobalStoreContext.Provider>
	);
};
